import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Collapse,
  Slider,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Switch,
  FormControlLabel,
  FormHelperText,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  MenuBook as KnowledgeIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';

import { ragService } from '../services/ragService';
import { agentsService } from '../services/agentsService'; // 🎯 PHASE 5: Para carregar modelos de embedding
import { ROUTES } from '../constants';
import RetrievalMethodExplanation from '../components/common/RetrievalMethodExplanation';

const CreateKnowledgeBase = () => {
  const navigate = useNavigate();
  const { kbId } = useParams();
  const isEditMode = Boolean(kbId);

  // Estados dos dados do formulário - seguindo DOCUMENTACAO_COMPLETA_RAG.md
  const [formData, setFormData] = useState({
    // Campos obrigatórios
    name: '',
    embedding_model: 'text-embedding-3-small', // 🎯 PHASE 5: Usar modelo real do backend
    retrieval_method: 'semantic',
    
    // Campos opcionais
    description: '',
    context: '',
    
    // Configurações avançadas de retrieval
    retrieval_config: {
      top_k: 5,
      threshold: 0.5,  // Reduzido para mostrar mais resultados relevantes
      scoring: {
        strategy: 'weighted_sum',
        weights: {
          semantic: 0.6,  // Prioriza significado (bom para maioria dos casos)
          bm25: 0.4,      // Complementa com palavras-chave (semântico + bm25 = 1.0)
          metadata: 0.1,  // Reduzido - informações do arquivo
          recency: 0.05,  // Reduzido - documentos recentes
          length: 0.02    // Reduzido - tamanho do chunk
        },
        boost_factors: {
          hybrid_consensus: 1.2,
          high_confidence: 1.15,
          perfect_match: 1.3,
          recent_doc: 1.1,
          optimal_length: 1.05
        }
      }
    }
  });

  // Estados de controle
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  
  // 🎯 PHASE 5: Estado para modelos de embedding disponíveis
  const [availableEmbeddingModels, setAvailableEmbeddingModels] = useState([]);
  const [modelsLoaded, setModelsLoaded] = useState(false); // 🔧 FIX: Controlar carregamento dos modelos



  // 🎯 PHASE 5: Carregar modelos de embedding disponíveis
  const loadAvailableEmbeddingModels = async () => {
    try {
      setModelsLoaded(false); // 🔧 FIX: Marcar como carregando
      const models = await agentsService.getAvailableModelsByType('embedding');
      setAvailableEmbeddingModels(models);
      console.log('🔧 [FIX] Modelos de embedding carregados:', models);
    } catch (err) {
      console.error('Erro ao carregar modelos de embedding:', err);
      // Fallback para modelos de embedding padrão
      setAvailableEmbeddingModels([
        { value: 'text-embedding-3-small', label: 'text-embedding-3-small (1536 dim)' },
        { value: 'text-embedding-3-large', label: 'text-embedding-3-large (3072 dim)' },
      ]);
    } finally {
      setModelsLoaded(true); // 🔧 FIX: Marcar como carregado
      console.log('🔧 [FIX] Modelos de embedding marcados como carregados');
    }
  };

  // Carregar modelos disponíveis
  useEffect(() => {
    loadAvailableEmbeddingModels();
  }, []);

  // 🔧 FIX: Carregar dados da KB APENAS após modelos estarem carregados
  useEffect(() => {
    if (isEditMode && modelsLoaded) {
      console.log('🔧 [FIX] Carregando dados da KB após modelos carregados');
      loadKnowledgeBaseData();
    }
  }, [kbId, modelsLoaded]); // Dependências: kbId E modelsLoaded

  // 🔧 FIX: Monitorar mudanças no embedding_model para debug
  useEffect(() => {
    console.log('🔧 [FIX] embedding_model mudou para:', formData.embedding_model);
    console.log('🔧 [FIX] Modelos disponíveis para comparar:', availableEmbeddingModels.map(m => m.value));
  }, [formData.embedding_model, availableEmbeddingModels]);

  const loadKnowledgeBaseData = async () => {
    try {
      setLoading(true);
      const data = await ragService.getKnowledgeBase(kbId);
      console.log('🔧 [FIX] Dados carregados da KB:', data);
      console.log('🔧 [FIX] embedding_model da KB:', data.embedding_model);
      if (!data) {
        setError('Base de conhecimento não encontrada.');
        setLoading(false);
        return;
      }

      const newFormData = {
        name: data.name || '',
        description: data.description || '',
        context: data.context || '',
        embedding_model: data.embedding_model || 'text-embedding-3-small', // 🎯 PHASE 5: Usar modelo real
        retrieval_method: data.retrieval_method || 'semantic',
        retrieval_config: data.retrieval_config || formData.retrieval_config
      };
      
      console.log('🔧 [FIX] FormData que será setado:', newFormData);
      console.log('🔧 [FIX] Modelos disponíveis atuais:', availableEmbeddingModels);
      
      setFormData(newFormData);
    } catch (err) {
      console.error('Erro ao carregar base de conhecimento:', err);
      setError('Erro ao carregar dados da base de conhecimento.');
    } finally {
      setLoading(false);
    }
  };

  // Validação do formulário seguindo as especificações
  const validateForm = () => {
    const errors = {};

    // Campos obrigatórios
        if (!formData.name.trim()) {
          errors.name = 'Nome é obrigatório';
    } else if (formData.name.length > 255) {
      errors.name = 'Nome deve ter no máximo 255 caracteres';
    }

    if (!formData.embedding_model) {
      errors.embedding_model = 'Modelo de embedding é obrigatório';
    }

    if (!formData.retrieval_method) {
      errors.retrieval_method = 'Método de retrieval é obrigatório';
    }

    // Validação de configurações avançadas
    const config = formData.retrieval_config;
    if (config.top_k < 1 || config.top_k > 20) {
      errors.top_k = 'Top K deve estar entre 1 e 20';
    }

    if (config.threshold < 0.0 || config.threshold > 1.0) {
      errors.threshold = 'Threshold deve estar entre 0.0 e 1.0';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Manipulação de dados do formulário
  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      // Para campos aninhados (retrieval_config)
      const keys = field.split('.');
      setFormData(prev => {
        const newData = { ...prev };
        let current = newData;
        
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        
        return newData;
      });
    } else {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    }
    
    // Limpar erro de validação
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Salvar base de conhecimento
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let result;
      if (isEditMode) {
        result = await ragService.updateKnowledgeBase(kbId, formData);
      } else {
        result = await ragService.createKnowledgeBase(formData);
      }

      setSuccess(true);
      
      // Navegar para detalhes após sucesso
      setTimeout(() => {
        const targetKbId = isEditMode ? kbId : result.id;
        navigate(ROUTES.KNOWLEDGE_BASE_DETAIL.replace(':kbId', targetKbId));
      }, 1500);

    } catch (err) {
      console.error('Erro ao salvar base de conhecimento:', err);
      setError(err.response?.data?.detail?.message || 'Erro ao salvar base de conhecimento.');
    } finally {
      setLoading(false);
    }
  };



  if (isEditMode && (!modelsLoaded || loading)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTES.KNOWLEDGE_BASE)}
            sx={{ mr: 2 }}
          >
            Voltar
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: 'primary.light',
                color: 'primary.main',
              }}
            >
              <KnowledgeIcon />
            </Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {isEditMode ? 'Editar Base de Conhecimento' : 'Nova Base de Conhecimento'}
          </Typography>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          {isEditMode 
            ? 'Atualize as configurações da base de conhecimento'
            : 'Configure uma nova base de conhecimento para seus agentes'
          }
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Base de conhecimento {isEditMode ? 'atualizada' : 'criada'} com sucesso! 
          Redirecionando...
        </Alert>
      )}

      {/* Formulário Principal */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Configurações Básicas
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* 1. Nome (obrigatório) */}
              <TextField
                label="Nome da Base de Conhecimento"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!validationErrors.name}
                helperText={validationErrors.name || 'Nome único por empresa (1-255 caracteres)'}
                fullWidth
                required
                inputProps={{ maxLength: 255 }}
              />

              {/* 2. Descrição (opcional) */}
              <TextField
                label="Descrição"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={3}
                fullWidth
                helperText="Descrição detalhada da base de conhecimento"
              />

              {/* 3. Contexto (opcional) */}
              <TextField
                label="Contexto para Agente"
                value={formData.context}
                onChange={(e) => handleInputChange('context', e.target.value)}
                multiline
                rows={2}
                fullWidth
                placeholder="Quando o agente deve usar esta base?"
                helperText="Contexto que ajuda o agente a escolher quando usar esta base"
              />

              {/* 4. Modelo de Embedding (obrigatório) */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    Modelo de Embedding *
                  </Typography>
                  <Tooltip title="Modelo de IA usado para converter texto em vetores numéricos. Modelos maiores são mais precisos mas custam mais." placement="right">
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <FormControl fullWidth required>
                  <Select
                    key={`select-${availableEmbeddingModels.length}-${formData.embedding_model}`}
                    value={formData.embedding_model}
                    onChange={(e) => {
                      console.log('🔧 [FIX] Select onChange:', e.target.value);
                      handleInputChange('embedding_model', e.target.value);
                    }}
                    error={!!validationErrors.embedding_model}
                    onOpen={() => console.log('🔧 [FIX] Select aberto, modelos disponíveis:', availableEmbeddingModels)}
                  >
                    {/* 🎯 PHASE 5: Modelos dinâmicos do backend */}
                    {availableEmbeddingModels.map((model) => {
                      console.log('🔧 [FIX] Renderizando MenuItem:', model.value, '- Label:', model.label);
                      return (
                        <MenuItem key={model.value} value={model.value}>
                          <Box>
                            <Typography variant="body2">{model.label}</Typography>
                            {model.input_price_usd && (
                              <Typography variant="caption" color="text.secondary">
                                ${(model.input_price_usd * 1000000).toFixed(2)}/1M tokens
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                    
                    {/* Fallback se nenhum modelo carregado */}
                    {availableEmbeddingModels.length === 0 && (
                      <MenuItem value="text-embedding-3-small">
                        <Box>
                          <Typography variant="body2">text-embedding-3-small (Carregando...)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            1536 dimensões
                          </Typography>
                        </Box>
                      </MenuItem>
                    )}
                  </Select>
                  <FormHelperText error={!!validationErrors.embedding_model}>
                    {validationErrors.embedding_model || 'Modelo usado para gerar embeddings'}
                  </FormHelperText>
                </FormControl>
              </Box>

              {/* 5. Método de Retrieval (obrigatório) */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    Método de Retrieval *
                  </Typography>
                  <RetrievalMethodExplanation tooltipOnly />
                </Box>
                <FormControl fullWidth required>
                  <Select
                    value={formData.retrieval_method}
                    onChange={(e) => handleInputChange('retrieval_method', e.target.value)}
                    error={!!validationErrors.retrieval_method}
                  >
                    <MenuItem value="semantic">
                      <Box>
                        <Typography variant="body2">Semântico</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Entende significados e conceitos
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="hybrid">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box>
                          <Typography variant="body2">Híbrido</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Combina semântica + palavras-chave com escolha automática
                          </Typography>
                        </Box>
                        <Chip label="Recomendado" size="small" color="success" />
                      </Box>
                    </MenuItem>
                    <MenuItem value="lexical">
                      <Box>
                        <Typography variant="body2">Lexical (BM25)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Busca por palavras-chave exatas
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                  <FormHelperText error={!!validationErrors.retrieval_method}>
                    {validationErrors.retrieval_method || 'Método de busca padrão'}
                  </FormHelperText>
                </FormControl>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* 6. Configurações Avançadas (collapsible panel) */}
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
              onClick={() => setAdvancedExpanded(!advancedExpanded)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Configurações Avançadas
                </Typography>
                <Tooltip title="Configurações de scoring e performance">
                  <InfoIcon color="action" sx={{ fontSize: 18 }} />
                </Tooltip>
              </Box>
              <IconButton>
                {advancedExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={advancedExpanded}>
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Top K resultados */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Top K Resultados: {formData.retrieval_config.top_k}
                  </Typography>
                  <Slider
                    value={formData.retrieval_config.top_k}
                    onChange={(e, value) => handleInputChange('retrieval_config.top_k', value)}
                    min={1}
                    max={20}
                    marks={[
                      { value: 1, label: '1' },
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                      { value: 20, label: '20' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Número máximo de resultados retornados
                  </Typography>
                </Box>

                {/* Threshold score */}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Threshold Score: {formData.retrieval_config.threshold.toFixed(2)}
                  </Typography>
                  <Slider
                    value={formData.retrieval_config.threshold}
                    onChange={(e, value) => handleInputChange('retrieval_config.threshold', value)}
                    min={0.0}
                    max={1.0}
                    step={0.1}
                    marks={[
                      { value: 0.0, label: '0.0' },
                      { value: 0.5, label: '0.5' },
                      { value: 0.7, label: '0.7' },
                      { value: 1.0, label: '1.0' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Score mínimo para considerar um resultado relevante
                  </Typography>
                </Box>

                {/* Pesos para busca híbrida */}
                {formData.retrieval_method === 'hybrid' && (
                  <Paper sx={{ p: 3, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main', mb: 3 }}>
                      Configuração de Pesos (Busca Híbrida)
                    </Typography>
                    
                    {/* Seção: Pesos Principais (Semântico + BM25) */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 2, color: 'primary.main' }}>
                        Pesos Principais
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                              Semântico: {formData.retrieval_config.scoring.weights.semantic.toFixed(2)}
                            </Typography>
                            <Tooltip title="Controla a importância da busca por significado e conceitos. Aumente para: documentação, FAQs, conteúdo explicativo." placement="top">
                              <IconButton size="small">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Slider
                            value={formData.retrieval_config.scoring.weights.semantic}
                            onChange={(e, newValue) => {
                              const bm25Value = 1 - newValue;
                              handleInputChange('retrieval_config.scoring.weights.semantic', newValue);
                              handleInputChange('retrieval_config.scoring.weights.bm25', bm25Value);
                            }}
                            min={0.1}
                            max={0.9}
                            step={0.05}
                            size="small"
                            marks={[
                              { value: 0.1, label: '0.1' },
                              { value: 0.5, label: '0.5' },
                              { value: 0.9, label: '0.9' }
                            ]}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Prioriza busca por significado e conceitos
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                              BM25: {formData.retrieval_config.scoring.weights.bm25.toFixed(2)}
                            </Typography>
                            <Tooltip title="Controla a importância da busca por palavras-chave exatas. Aumente para: códigos, normas, termos técnicos específicos." placement="top">
                              <IconButton size="small">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Slider
                            value={formData.retrieval_config.scoring.weights.bm25}
                            onChange={(e, newValue) => {
                              const semanticValue = 1 - newValue;
                              handleInputChange('retrieval_config.scoring.weights.bm25', newValue);
                              handleInputChange('retrieval_config.scoring.weights.semantic', semanticValue);
                            }}
                            min={0.1}
                            max={0.9}
                            step={0.05}
                            size="small"
                            marks={[
                              { value: 0.1, label: '0.1' },
                              { value: 0.5, label: '0.5' },
                              { value: 0.9, label: '0.9' }
                            ]}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Prioriza busca por palavras-chave exatas
                          </Typography>
                        </Grid>
                      </Grid>
                      
                      <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
                        <Typography variant="caption">
                          <strong>Conectados:</strong> Semântico + BM25 sempre somam 1.0. Ajustar um altera automaticamente o outro.
                        </Typography>
                      </Alert>
                    </Box>

                    {/* Seção: Pesos Secundários */}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 2, color: 'text.secondary' }}>
                        Pesos Secundários (Opcional)
                      </Typography>
                      
                      {['metadata', 'recency', 'length'].map((key) => {
                        const labels = {
                          metadata: 'Metadados',
                          recency: 'Recência',
                          length: 'Tamanho'
                        };
                        
                        const tooltips = {
                          metadata: 'Usa informações do arquivo (nome, tipo). Aumente para: quando o nome do arquivo é importante, documentos categorizados.',
                          recency: 'Prioriza documentos mais recentes. Aumente para: bases que recebem atualizações frequentes, informações que expiram.',
                          length: 'Prioriza chunks de tamanho ideal (nem muito pequenos, nem muito grandes). Geralmente manter baixo.'
                        };
                        
                        const value = formData.retrieval_config.scoring.weights[key];
                        
                        return (
                          <Box key={key} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                                {labels[key]}: {value.toFixed(2)}
                              </Typography>
                              <Tooltip title={tooltips[key]} placement="top">
                                <IconButton size="small">
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Slider
                              value={value}
                              onChange={(e, newValue) => 
                                handleInputChange(`retrieval_config.scoring.weights.${key}`, newValue)
                              }
                              min={0.0}
                              max={0.3}
                              step={0.05}
                              size="small"
                              marks={[
                                { value: 0, label: '0' },
                                { value: 0.15, label: '0.15' },
                                { value: 0.3, label: '0.3' }
                              ]}
                            />
                          </Box>
                        );
                      })}
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>
                      <strong>Dica:</strong> Para a maioria dos casos, os valores padrão funcionam bem. Ajuste apenas se necessário.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Botões de ação */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => navigate(ROUTES.KNOWLEDGE_BASE)}
            disabled={loading}
          >
            Cancelar
          </Button>
          
            <Button
            variant="contained"
            onClick={handleSave}
              disabled={loading || success}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            sx={{ minWidth: 120 }}
            >
              {loading ? 'Salvando...' : isEditMode ? 'Salvar' : 'Criar'}
            </Button>
        </Box>
      </motion.div>


    </Box>
  );
};

export default CreateKnowledgeBase; 